import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import OrderDetailModal from './OrderDetailModal';
import PrintModal from './PrintModal';
import './Inventory.css';

const Inventory = () => {
    const [kitTypes, setKitTypes] = useState([]);
    const [selectedKitType, setSelectedKitType] = useState(null);
    const [kitUnits, setKitUnits] = useState([]);
    const [filteredKitUnits, setFilteredKitUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [unitsFilter, setUnitsFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const BASE_URL = 'https://service9004.szapfs.org';

    // Fetch kit types on component mount
    useEffect(() => {
        fetchKitTypes();
        fetchOrders();
    }, []);

    const fetchKitTypes = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}/api/kittypes`);
            if (!response.ok) throw new Error('Failed to fetch kit types');
            const data = await response.json();
            setKitTypes(data);
            setError(null);
        } catch (err) {
            setError('Failed to load kit types');
            console.error('Error fetching kit types:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrders = async () => {
        try {
            const response = await fetch(`${BASE_URL}/api/orders`);
            if (!response.ok) throw new Error('Failed to fetch orders');
            const data = await response.json();
            setOrders(data);
        } catch (err) {
            console.error('Error fetching orders:', err);
        }
    };

    const fetchKitUnits = async (kitTypeId) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${BASE_URL}/api/kittypes/${kitTypeId}/kitunits`);
            if (!response.ok) throw new Error('Failed to fetch kit units');
            const data = await response.json();
            setKitUnits(data);
            setFilteredKitUnits(data);
        } catch (err) {
            console.error('Error fetching kit units:', err);
            setKitUnits([]);
            setFilteredKitUnits([]);
        } finally {
            setActionLoading(false);
        }
    };

    // Filter kit units based on search and status
    useEffect(() => {
        let filtered = [...kitUnits];
        
        // Apply text filter
        if (unitsFilter.trim()) {
            const filterLower = unitsFilter.toLowerCase();
            filtered = filtered.filter(unit => 
                unit.KitUnitID.toLowerCase().includes(filterLower) ||
                unit.Status.toLowerCase().includes(filterLower) ||
                (unit.UserID && unit.UserID.toString().includes(filterLower))
            );
        }
        
        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(unit => 
                unit.Status.toLowerCase() === statusFilter.toLowerCase()
            );
        }
        
        setFilteredKitUnits(filtered);
    }, [kitUnits, unitsFilter, statusFilter]);

    const handleKitTypeClick = (kitType) => {
        setSelectedKitType(kitType);
        fetchKitUnits(kitType.KitID);
    };

    const handleUnitClick = (unit) => {
        const order = orders.find(o => o.OrderID === unit.OrderID);
        setSelectedUnit({ ...unit, orderDetails: order });
        setShowOrderModal(true);
    };

    const addKitUnits = async (kitTypeId, quantity) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${BASE_URL}/api/kittypes/${kitTypeId}/kitunits`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ quantity }),
            });
            if (!response.ok) throw new Error('Failed to add kit units');
            
            // Refresh kit units after adding
            fetchKitUnits(kitTypeId);
        } catch (err) {
            console.error('Error adding kit units:', err);
        } finally {
            setActionLoading(false);
        }
    };

    const deleteKitUnit = async (unitId) => {
        try {
            setActionLoading(true);
            const response = await fetch(`${BASE_URL}/api/kitunits/${unitId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete kit unit');
            
            // Refresh kit units after deleting
            if (selectedKitType) {
                fetchKitUnits(selectedKitType.KitID);
            }
        } catch (err) {
            console.error('Error deleting kit unit:', err);
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString();
    };

    const getStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'activated': return 'GORDU-status-text-activated';
            case 'inactive': return 'GORDU-status-text-inactive';
            case 'pending': return 'GORDU-status-text-pending';
            default: return 'GORDU-status-text-default';
        }
    };

// src/components/Inventory.js

const handlePrintLabels = async (quantity) => {
    if (!selectedKitType) {
        setError("No kit type selected.");
        return;
    }

    setActionLoading(true);
    setError(null);

    try {
        // Step 1: Call the existing endpoint to create exactly 'quantity' new units.
        const response = await fetch(`${BASE_URL}/api/kittypes/${selectedKitType.KitID}/kitunits`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity }),
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to generate new kit units');
        }

        // Step 2: Use the response directly to prepare for printing.
        // The backend conveniently returns the IDs it just created.
        const { generated_ids } = data;
        if (!generated_ids || generated_ids.length === 0) {
            throw new Error('API did not return the new kit IDs.');
        }

        const unitsToPrint = generated_ids.map(id => ({ ActivationID: id }));

        // Step 3: Open the local print view with the new units.
        openPrintView(unitsToPrint);
        
        // Step 4: Close the modal and refresh the unit list in the background.
        setShowPrintModal(false);
        fetchKitUnits(selectedKitType.KitID);

    } catch (err) {
        console.error('Error printing labels:', err);
        setError(err.message);
    } finally {
        // The setActionLoading(false) will be handled by the fetchKitUnits call,
        // but we can set it here too as a fallback.
        setActionLoading(false);
    }
};

    const openPrintView = (units) => {
        // Clean up any previous iframe to be safe
        const oldIframe = document.getElementById('print-iframe-labels');
        if (oldIframe) {
            oldIframe.remove();
        }

        // Create an invisible iframe
        const iframe = document.createElement('iframe');
        iframe.id = 'print-iframe-labels';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        // Write the content to be printed into the iframe
        iframe.contentDocument.write(`
            <html>
                <head>
                    <title>Print Kit Labels</title>
                    <style>
                        body { font-family: sans-serif; }
                        .label-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
                        .label { border: 1px solid #ccc; padding: 10px; text-align: center; page-break-inside: avoid; }
                        .activation-id { font-weight: bold; font-size: 1.2em; }
                        @media print {
                            body { margin: 0.5in; }
                            .label-grid { gap: 15px; }
                        }
                    </style>
                </head>
                <body>
                    <div class="label-grid">
                        ${units.map(unit => `
                            <div class="label">
                                <div>Kit Unit ID:</div>
                                <div class="activation-id">${unit.ActivationID || unit.KitUnitID.slice(-8)}</div>
                            </div>
                        `).join('')}
                    </div>
                </body>
            </html>
        `);
        iframe.contentDocument.close();

        // Set up cleanup to run after printing is done
        iframe.contentWindow.onafterprint = () => {
            iframe.remove();
        };

        // Focus and print
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
    };

    if (loading) {
        return (
            <div className="GORDU-inventory-container">
                <div className="GORDU-loading">Loading inventory...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="GORDU-inventory-container">
                <div className="GORDU-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="GORDU-inventory-container">
            {/* Left Panel - Kit Types */}
            <div className="GORDU-kit-types-panel">
                <div className="GORDU-panel-header">
                    <div className="GORDU-header-content">
                        <i className="fas fa-boxes GORDU-header-icon"></i>
                        <span className="GORDU-header-title">Kit Types</span>
                    </div>
                    <div className="GORDU-kit-count">{kitTypes.length}</div>
                </div>
                <div className="GORDU-kit-types-list">
                    {kitTypes.map((kitType) => (
                        <div
                            key={kitType.KitID}
                            className={`GORDU-kit-type-chip ${selectedKitType?.KitID === kitType.KitID ? 'GORDU-selected' : ''}`}
                            onClick={() => handleKitTypeClick(kitType)}
                        >
                            <div className="GORDU-kit-image-small">
                                <img 
                                    src={kitType.ImageURL} 
                                    alt={kitType.Name}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                                <div className="GORDU-kit-fallback-icon" style={{display: 'none'}}>
                                    <i className="fas fa-flask"></i>
                                </div>
                            </div>
                            <div className="GORDU-kit-info-compact">
                                <div className="GORDU-kit-name-small">{kitType.Name}</div>
                
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Panel - Kit Units */}
            <div className="GORDU-kit-units-panel">
                {selectedKitType ? (
                    <>
                        <div className="GORDU-panel-header">
                            <div className="GORDU-header-content">
                                <i className="fas fa-cubes GORDU-header-icon"></i>
                                <div>
                                    <span className="GORDU-header-title">{selectedKitType.Name}</span>
                                    <div className="GORDU-panel-subtitle">{filteredKitUnits.length} of {kitUnits.length} units</div>
                                </div>
                            </div>
                            <div className="GORDU-header-actions">
                                <button 
                                    className="GORDU-btn GORDU-btn-secondary"
                                    onClick={() => setShowPrintModal(true)}
                                    disabled={actionLoading}
                                >
                                    <i className="fas fa-print"></i>
                                    Print Labels
                                </button>
                                <button 
                                    className="GORDU-btn GORDU-btn-primary"
                                    onClick={() => {
                                        const quantity = prompt('How many units would you like to add?');
                                        if (quantity && !isNaN(quantity) && quantity > 0) {
                                            addKitUnits(selectedKitType.KitID, parseInt(quantity));
                                        }
                                    }}
                                    disabled={actionLoading}
                                >
                                    <i className="fas fa-plus"></i>
                                    Add Units
                                </button>
                            </div>
                        </div>
                        
                        {/* Filters */}
                        <div className="GORDU-filters-container">
                            <div className="GORDU-search-container">
                                <i className="fas fa-search GORDU-search-icon"></i>
                                <input
                                    type="text"
                                    className="GORDU-search-input"
                                    placeholder="Search units..."
                                    value={unitsFilter}
                                    onChange={(e) => setUnitsFilter(e.target.value)}
                                />
                            </div>
                            <div className="GORDU-status-filter">
                                <select 
                                    className="GORDU-filter-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="all">All Status</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>
                        </div>
                        
                        {actionLoading ? (
                            <div className="GORDU-loading">
                                <i className="fas fa-spinner fa-spin"></i>
                                Loading units...
                            </div>
                        ) : (
                            <div className="GORDU-kit-units-grid">
                                {filteredKitUnits.length === 0 ? (
                                    <div className="GORDU-empty-state">
                                        <i className="fas fa-inbox GORDU-empty-icon"></i>
                                        <div className="GORDU-empty-title">
                                            {kitUnits.length === 0 ? 'No Units Available' : 'No matching units'}
                                        </div>
                                        <div className="GORDU-empty-subtitle">
                                            {kitUnits.length === 0 ? 'Add some units to get started' : 'Try adjusting your filters'}
                                        </div>
                                    </div>
                                ) : (
                                    filteredKitUnits.map((unit) => (
                                        <div
                                            key={unit.KitUnitID}
                                            className="GORDU-unit-chip"
                                            onClick={() => handleUnitClick(unit)}
                                        >
                                            <div className="GORDU-unit-chip-left">
                                                <div className="GORDU-unit-activation-id">
                                                    {unit.ActivationID || unit.KitUnitID.slice(-8)}
                                                </div>
                                                <div className="GORDU-unit-creation-date">
                                                    {formatDate(unit.CreationDate)}
                                                </div>
                                            </div>
                                            <div 
                                                className={`GORDU-unit-status-chip ${getStatusClass(unit.Status)}`}
                                            >
                                                {unit.Status}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="GORDU-empty-state">
                        <i className="fas fa-arrow-left GORDU-empty-icon"></i>
                        <div className="GORDU-empty-title">Select a Kit Type</div>
                        <div className="GORDU-empty-subtitle">Choose a kit type from the left panel to view its units</div>
                    </div>
                )}
            </div>

            {/* Order Detail Modal via Portal */}
            {showOrderModal && selectedUnit && createPortal(
                <OrderDetailModal
                    unit={selectedUnit}
                    isOpen={showOrderModal}
                    onClose={() => {
                        setShowOrderModal(false);
                        setSelectedUnit(null);
                    }}
                />,
                document.body
            )}


{showPrintModal && selectedKitType && createPortal(
    <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        onPrint={handlePrintLabels} // The handler is now simpler
        kitType={selectedKitType}
        actionLoading={actionLoading}
        // REMOVED: availableUnits is no longer needed
    />,
    document.body
)}
        </div>
    );
};

export default Inventory;
